import { EU_COUNTRIES } from "@logiful/domain";
import { Check, Copy, ExternalLink, Globe2, Warehouse, Workflow } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";
import { ChannelMark } from "@/components/ChannelLogo";
import { CarrierMark } from "@/components/CarrierMark";
import { Flag } from "@/components/Flag";
import { Highlight } from "@/components/Highlight";
import { cx, Tip } from "@/components/ui";
import type { OrderItem } from "@/lib/api";
import { countryName, fmtDateTime, fmtMoney, fmtNumber, fmtRelative, trackingUrl } from "@/lib/format";
import { msg, t } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";

/** frei wählbare Spalten der Auftragsliste (Auftrag und Status sind immer da) */
export const ORDER_COLUMN_KEYS = ["recipient", "country", "channel", "details", "warehouse", "shippingMethod", "payment", "items", "weight", "total", "tags", "backendRef", "tracking", "placed", "shipped", "email", "priority"] as const;
export type OrderColumnKey = (typeof ORDER_COLUMN_KEYS)[number];
export const ORDER_COLUMN_DEFAULTS: OrderColumnKey[] = ["recipient", "details", "warehouse", "total", "placed"];

export interface OrderCellCtx {
  needles: string[];
  methodName: (id: string | null | undefined) => string | null;
}
interface ColumnDef {
  key: OrderColumnKey;
  label: string;
  group: string;
  align?: "right";
  /** Text für den CSV-Export */
  text: (o: OrderItem, c: OrderCellCtx) => string;
  cell: (o: OrderItem, c: OrderCellCtx) => ReactNode;
}

const PAYMENT: Record<string, string> = { paid: msg("bezahlt"), pending: msg("offen"), authorized: msg("autorisiert"), partially_paid: msg("teilweise bezahlt"), refunded: msg("erstattet"), voided: msg("storniert"), unknown: "—" };
const dash = <span className="text-fg-subtle">—</span>;
const kg = (o: OrderItem) => (o.weightKg == null ? "" : `${o.weightIncomplete ? "≥ " : ""}${fmtNumber(o.weightKg)} kg`);

/**
 * Nur die Ausnahme markieren: Export (Zoll, ggf. Ausfuhrbegleitdokument) – Land im Tooltip. EU-Aufträge bleiben
 * ohne Zeichen (eine Flagge in jeder Zeile sagt nichts); das Land zeigt die Spalte „Land“.
 */
function ZoneMark({ country }: { country: string | null | undefined }) {
  if (!country || EU_COUNTRIES.has(country.toUpperCase())) return null;
  const label = `${t("Export")} · ${countryName(country)}`;
  return <span title={label} className="inline-flex text-warning"><Globe2 className="size-3.5" aria-label={label} /></span>;
}

export const ORDER_COLUMNS: ColumnDef[] = [
  {
    key: "recipient", label: msg("Empfänger"), group: msg("Empfänger"),
    text: (o) => o.customerName,
    cell: (o, c) => (
      <>
        <span className="inline-flex items-center gap-1.5"><ZoneMark country={o.country} /><Highlight text={o.customerName} needles={c.needles} /></span>
        {o.match && (
          <div className="mt-0.5 text-[11.5px] text-fg-muted truncate max-w-[260px]">
            <span className="text-fg-subtle">{o.match.field}:</span> <span className={cx(["SKU", "Tracking", "Lager-Nr.", "Shopify"].includes(o.match.field) && "font-mono")}><Highlight text={o.match.value} needles={c.needles} /></span>
          </div>
        )}
      </>
    ),
  },
  { key: "country", label: msg("Land"), group: msg("Empfänger"), text: (o) => o.country, cell: (o) => <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Flag country={o.country} title={false} />{countryName(o.country)}</span> },
  { key: "email", label: msg("E-Mail"), group: msg("Empfänger"), text: (o) => o.email ?? "", cell: (o) => (o.email ? <span className="text-[12.5px]">{o.email}</span> : dash) },
  { key: "channel", label: msg("Shop"), group: msg("Auftrag"), text: (o) => o.channelName, cell: (o) => <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ChannelMark kind={o.channelKind} />{o.channelName}</span> },
  {
    key: "details", label: msg("Details"), group: msg("Auftrag"),
    text: (o) => (o.status === "on_hold" ? o.holdReasons.map(serverText).join(" · ") : (o.tracking?.numbers[0] ?? "")),
    cell: (o, c) =>
      o.status === "on_hold" ? (
        // der Grund steht schon am Status (Pille + Kurzfassung); hier nur noch, ob eine Regel beteiligt ist
        <span className="flex items-center gap-1.5 text-[12.5px] max-w-[340px]">
          {o.appliedRules.length === 0 && dash}
          {o.appliedRules.length > 0 && (
            <Tip content={<>{t("Durch Regel")}: {o.appliedRules.map((r) => r.name).join(", ")}</>}>
              <Workflow className="size-3.5 text-fg-subtle shrink-0" aria-label={t("Regel")} />
            </Tip>
          )}
        </span>
      ) : o.tracking ? (
        <TrackingCell carrier={o.tracking.carrier} numbers={o.tracking.numbers} needles={c.needles} />
      ) : dash,
  },
  { key: "tags", label: msg("Tags"), group: msg("Auftrag"), text: (o) => (o.tags ?? []).join(", "), cell: (o) => (o.tags?.length ? <span className="text-[12px] text-fg-muted line-clamp-2 max-w-[220px]">{o.tags.join(" · ")}</span> : dash) },
  { key: "priority", label: msg("Priorität"), group: msg("Auftrag"), align: "right", text: (o) => String(o.priority ?? 0), cell: (o) => (o.priority ? <span className="tabular-nums">{o.priority > 0 ? `+${o.priority}` : o.priority}</span> : dash) },
  { key: "payment", label: msg("Zahlstatus"), group: msg("Auftrag"), text: (o) => t(PAYMENT[o.financialStatus ?? "unknown"] ?? o.financialStatus ?? ""), cell: (o) => <span className={cx("text-[12.5px] whitespace-nowrap", o.financialStatus === "pending" && "text-warning")}>{t(PAYMENT[o.financialStatus ?? "unknown"] ?? o.financialStatus ?? "—")}</span> },
  { key: "items", label: msg("Stück"), group: msg("Ware"), align: "right", text: (o) => String(o.itemCount), cell: (o) => <span className="tabular-nums">{fmtNumber(o.itemCount)}</span> },
  { key: "weight", label: msg("Gewicht"), group: msg("Ware"), align: "right", text: kg, cell: (o) => (o.weightKg == null ? dash : <span className="tabular-nums whitespace-nowrap" title={o.weightIncomplete ? t("Mindestens – nicht alle Artikel haben ein Gewicht") : undefined}>{kg(o)}</span>) },
  { key: "total", label: msg("Wert"), group: msg("Ware"), align: "right", text: (o) => (o.replaced ? "" : (o.totalMinor / 100).toFixed(2)), cell: (o) => (o.replaced ? <span className="text-fg-subtle">—</span> : <span className="tabular-nums">{fmtMoney(o.totalMinor, o.currency)}</span>) },
  {
    key: "shippingMethod", label: msg("Versandart"), group: msg("Versand"),
    text: (o, c) => c.methodName(o.shippingMethodId) ?? o.channelShippingMethod ?? "",
    cell: (o, c) => {
      const name = c.methodName(o.shippingMethodId);
      return name || o.channelShippingMethod ? <span className="text-[12.5px] whitespace-nowrap">{name ?? <span className="text-fg-muted">{o.channelShippingMethod}</span>}</span> : dash;
    },
  },
  {
    key: "warehouse", label: msg("Lager"), group: msg("Versand"),
    text: (o) => o.warehouse?.code ?? "",
    cell: (o) => (o.warehouse ? <WarehouseBadge code={o.warehouse.code} name={o.warehouse.name} /> : dash),
  },
  { key: "backendRef", label: msg("Lager-Nr."), group: msg("Versand"), text: (o) => o.backendRef ?? "", cell: (o) => (o.backendRef ? <span className="font-mono text-[12px]">{o.backendRef}</span> : dash) },
  { key: "tracking", label: msg("Sendungsnummer"), group: msg("Versand"), text: (o) => (o.tracking?.numbers ?? []).join(" "), cell: (o) => (o.tracking?.numbers.length ? <span className="font-mono text-[12px]">{o.tracking.numbers.join(", ")}</span> : dash) },
  { key: "placed", label: msg("Bestellt"), group: msg("Zeit"), align: "right", text: (o) => o.placedAt, cell: (o) => <span className="text-[12px] text-fg-muted whitespace-nowrap">{fmtRelative(o.placedAt)}</span> },
  { key: "shipped", label: msg("Versendet am"), group: msg("Zeit"), align: "right", text: (o) => o.shippedAt ?? "", cell: (o) => (o.shippedAt ? <span className="text-[12px] text-fg-muted whitespace-nowrap">{fmtDateTime(o.shippedAt)}</span> : dash) },
];
export const ORDER_COLUMN_BY_KEY = Object.fromEntries(ORDER_COLUMNS.map((c) => [c.key, c])) as Record<OrderColumnKey, ColumnDef>;

/** Dienstleister aus dem Namen der Versandart („DHL LOGIFUL“ → „DHL“) – interne Versandart-Namen zeigen wir nicht */
export function carrierBrand(carrier: string | null | undefined): string {
  const c = carrier ?? "";
  if (/deutsche post|warenpost/i.test(c)) return "Deutsche Post";
  if (/dhl\s*express/i.test(c)) return "DHL Express";
  for (const [re, name] of [[/dhl/i, msg("DHL")], [/dpd/i, msg("DPD")], [/gls/i, msg("GLS")], [/ups/i, msg("UPS")], [/hermes/i, msg("Hermes")], [/fedex/i, msg("FedEx")], [/post\.at|österreichische post/i, msg("Österreichische Post")]] as const) if (re.test(c)) return name;
  return c.trim() || t("Versand");
}

/**
 * Versand in der Liste: nur der Dienstleister als Link zur Sendungsverfolgung – die Nummer steht im Auftrag.
 * Sucht jemand nach der Nummer, steht sie hervorgehoben da (warum ist das ein Treffer). Kopieren beim Drüberfahren.
 */
function TrackingCell({ carrier, numbers, needles }: { carrier: string; numbers: string[]; needles: string[] }) {
  const [copied, setCopied] = useState(false);
  const n = numbers[0] ?? "";
  const searched = needles.some((x) => x && numbers.some((nr) => nr.toLowerCase().includes(x.toLowerCase())));
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(numbers.join(", ")).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
      <a href={trackingUrl(carrier, n)} target="_blank" rel="noreferrer" title={`${carrierBrand(carrier)} – ${t("Sendung verfolgen")}`} className="inline-flex items-center gap-1.5 rounded-md hover:opacity-80">
        <CarrierMark brand={carrierBrand(carrier)} />
        {numbers.length > 1 && <span className="text-[11px] text-fg-subtle">{t("{n} Pakete", { n: numbers.length })}</span>}
        <ExternalLink className="size-3 text-fg-subtle" />
      </a>
      {searched && <span className="font-mono text-fg-muted"><Highlight text={numbers.find((nr) => needles.some((x) => x && nr.toLowerCase().includes(x.toLowerCase()))) ?? n} needles={needles} /></span>}
      <button type="button" onClick={copy} className="size-6 grid place-items-center rounded-md text-fg-subtle opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity hover:bg-surface-2 hover:text-fg" aria-label={t("Sendungsnummer kopieren")} title={t("Sendungsnummer kopieren")}>
        {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
      </button>
    </span>
  );
}

/** feste Farbe je Lager (aus dem Kürzel) – PDM und DDN sind so auf einen Blick unterscheidbar */
const WAREHOUSE_DOTS = ["#38bdf8", "#f59e0b", "#a78bfa", "#34d399", "#fb7185", "#22d3ee", "#facc15", "#f472b6"];
const dotOf = (code: string) => WAREHOUSE_DOTS[[...code].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % WAREHOUSE_DOTS.length];

/** Lagerkürzel als Badge (Name im Hinweis) – in Liste und Auftrag gleich */
export function WarehouseBadge({ code, name }: { code: string; name?: string | null }) {
  return (
    <span title={name ?? undefined} className="inline-flex items-center gap-1.5 h-6 pl-1.5 pr-2 rounded-lg bg-ink text-ink-fg text-[11.5px] font-semibold tracking-[0.06em] whitespace-nowrap shadow-sm">
      <Warehouse className="size-3.5 opacity-80" />
      {code}
      <span className="size-1.5 rounded-full" style={{ background: dotOf(code) }} aria-hidden="true" />
    </span>
  );
}
